import ObjectId from 'bson-objectid';
import { cseHeaders } from 'commons-ui/src/context/ajax/useAjaxProvider';
import config from '../../../config';
import { ApiResponse, RoomsListResponse, CreateRoomResponse, CreateRoomData, UpdateRoomData, UpdateRoomResponse } from './types';

const getHeaders = () => ({
	'Content-Type': 'application/json',
	Authorization: config.ADMIN_BASIC_AUTH,
	...cseHeaders()
});

export const roomApi = {
	async listActiveRooms(): Promise<ApiResponse<RoomsListResponse>> {
		const response = await fetch(`${config.ADMIN_API_URL}/room/listActiveRooms`, {
			method: 'GET',
			headers: getHeaders()
		});
		return response.json();
	},

	async createRoom(roomData: CreateRoomData): Promise<ApiResponse<CreateRoomResponse>> {
		const requestId = new ObjectId().toHexString();
		// Helper function to convert number string to number or null
		const toNumberOrNull = (value: string): number | null => {
			if (!value || value.trim() === '') return null;
			const num = Number(value);
			return Number.isNaN(num) ? null : num;
		};

		// Process tags - filter out empty strings and return null if array is empty
		const processTags = (tagsStr: string): string[] | null => {
			if (!tagsStr || tagsStr.trim() === '') return null;
			const tags = tagsStr
				.split(',')
				.map((tag) => tag.trim())
				.filter((tag) => tag !== '');
			return tags.length > 0 ? tags : null;
		};

		// Process segmentNames - filter out empty strings and return null if array is empty
		const processSegmentNames = (segmentNamesStr: string): string[] | null => {
			if (!segmentNamesStr || segmentNamesStr.trim() === '') return null;
			const segments = segmentNamesStr
				.split(',')
				.map((segment) => segment.trim())
				.filter((segment) => segment !== '');
			return segments.length > 0 ? segments : null;
		};

		// Process badge - return null if both icon and text are empty
		const processBadge = (icon: string, text: string): { icon: string; text: string } | null => {
			if ((!icon || icon.trim() === '') && (!text || text.trim() === '')) {
				return null;
			}
			return {
				icon: icon?.trim() || '',
				text: text?.trim() || ''
			};
		};

		// Build the request payload with required fields (always included, never null)
		const payload: any = {
			hostName: roomData.hostName.trim(),
			title: roomData.title.trim(),
			thumbnail: roomData.thumbnail.trim(),
			priorityOrder: Number(roomData.priorityOrder),
			_source: {
				requestId
			}
		};

		// Include hostId if provided, otherwise include referralCode
		if (roomData.hostId && roomData.hostId.trim() !== '') {
			payload.hostId = Number(roomData.hostId);
		} else if (roomData.referralCode && roomData.referralCode.trim() !== '') {
			payload.referralCode = roomData.referralCode.trim();
		}

		// Add optional fields only if they have non-empty values
		const tags = processTags(roomData.tags);
		if (tags !== null) {
			payload.tags = tags;
		}

		const badge = processBadge(roomData.badgeIcon, roomData.badgeText);
		if (badge !== null) {
			payload.badge = badge;
		}

		const membersAllowed = toNumberOrNull(roomData.membersAllowed);
		if (membersAllowed !== null) {
			payload.membersAllowed = membersAllowed;
		}

		const speakersAllowed = toNumberOrNull(roomData.speakersAllowed);
		if (speakersAllowed !== null) {
			payload.speakersAllowed = speakersAllowed;
		}

		const initialEntryFee = toNumberOrNull(roomData.initialEntryFee);
		if (initialEntryFee !== null) {
			payload.initialEntryFee = initialEntryFee;
		}

		const timeAllowedForSpeaker = toNumberOrNull(roomData.timeAllowedForSpeaker);
		if (timeAllowedForSpeaker !== null) {
			payload.timeAllowedForSpeaker = timeAllowedForSpeaker;
		}

		if (roomData.currencyType && roomData.currencyType.trim() !== '') {
			payload.currencyType = roomData.currencyType.trim();
		}

		const segmentNames = processSegmentNames(roomData.segmentNames);
		if (segmentNames !== null) {
			payload.segmentNames = segmentNames;
		}

		// Add new optional fields
		if (roomData.languages && roomData.languages.length > 0) {
			payload.languages = roomData.languages;
		}

		const charges = toNumberOrNull(roomData.charges);
		if (charges !== null) {
			payload.charges = charges;
		}

		const durationInMinutes = toNumberOrNull(roomData.durationInMinutes);
		if (durationInMinutes !== null) {
			payload.durationInMinutes = durationInMinutes;
		}

		if (roomData.roomType && roomData.roomType.trim() !== '') {
			payload.roomType = roomData.roomType.trim();
		}

		// Filter out null and undefined values (only affects optional fields)
		// Required fields are already set above and won't be null
		const filteredPayload = Object.fromEntries(
			Object.entries(payload).filter(([, value]) => value !== null && value !== undefined)
		);

		const response = await fetch(`${config.ADMIN_API_URL}/room/createRoom`, {
			method: 'POST',
			headers: getHeaders(),
			body: JSON.stringify(filteredPayload)
		});
		return response.json();
	},

	async endRoom(_id: string): Promise<ApiResponse<{ message: string }>> {
		const response = await fetch(`${config.ADMIN_API_URL}/room/endRoom`, {
			method: 'POST',
			headers: getHeaders(),
			body: JSON.stringify({
				_id
			})
		});
		return response.json();
	},

	async deleteRoom(hostRoomId: string): Promise<ApiResponse<{ message: string }>> {
		const response = await fetch(`${config.ADMIN_API_URL}/room/deleteRoom`, {
			method: 'POST',
			headers: getHeaders(),
			body: JSON.stringify({
				hostRoomId
			})
		});
		return response.json();
	},

	async updateRoom(roomData: UpdateRoomData): Promise<ApiResponse<UpdateRoomResponse>> {
		const requestId = new ObjectId().toHexString();

		// Process tags - always return array (empty if no tags)
		const processTags = (tagsStr: string): string[] => {
			if (!tagsStr || tagsStr.trim() === '') return [];
			const tags = tagsStr
				.split(',')
				.map((tag) => tag.trim())
				.filter((tag) => tag !== '');
			return tags;
		};

		// Process segmentNames - always return array (empty if no segments)
		const processSegmentNames = (segmentNamesStr: string): string[] => {
			if (!segmentNamesStr || segmentNamesStr.trim() === '') return [];
			const segments = segmentNamesStr
				.split(',')
				.map((segment) => segment.trim())
				.filter((segment) => segment !== '');
			return segments;
		};

		// Build the request payload with all fields
		// Always include ALL fields so backend knows to update them (even to empty values)
		const payload: any = {
			hostId: Number(roomData.hostId),
			hostRoomId: roomData.hostRoomId,
			title: roomData.title?.trim() || '',
			hostName: roomData.hostName?.trim() || '',
			tags: processTags(roomData.tags),
			bio: roomData.bio?.trim() || '',
			priorityOrder: roomData.priorityOrder ? Number(roomData.priorityOrder) : 0,
			badge: {
				bgUrl: roomData.badgeIcon?.trim() || '',
				text: roomData.badgeText?.trim() || ''
			},
			segmentNames: processSegmentNames(roomData.segmentNames),
			languages: roomData.languages || [],
			charges: roomData.charges ? Number(roomData.charges) : 0,
			durationInMinutes: roomData.durationInMinutes ? Number(roomData.durationInMinutes) : 0,
			_source: {
				requestId
			}
		};

		const response = await fetch(`${config.ADMIN_API_URL}/room/updateRoomDetails`, {
			method: 'POST',
			headers: getHeaders(),
			body: JSON.stringify(payload)
		});
		return response.json();
	}
};
